/**
 * Feature Discovery Service
 * Manages contextual "Did You Know?" tips and tracks what users have discovered
 */

import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { AppKnowledgeService } from './app-knowledge.service';
import { DiscoverableFeature } from '../models/feature-discovery.models';

@Injectable({ providedIn: 'root' })
export class FeatureDiscoveryService {

  // Track what user has discovered/used
  private discoveredFeatures = new Set<string>();
  private discoveredFeatures$ = new BehaviorSubject<Set<string>>(new Set());

  // Feature flags (could be loaded from backend)
  // All registry-driven discoverables are enabled by default
  // This set is only for gating features behind feature flags (discoverable.featureFlag)
  private enabledFeatures = new Set<string>();

  constructor(
    private appKnowledge: AppKnowledgeService
  ) {
    this.loadDiscoveredFeatures();
  }

  /**
   * Get contextual tips for current route (empty state)
   * Supports partial route matching: /leads/inactive-leads will match /leads/all-leads tips
   */
  getDiscoveryTips(route: string, limit = 3): DiscoverableFeature[] {
    // Try exact match first
    let routeDef = this.appKnowledge.getRoute(route);

    // If no exact match, try to find best matching route
    if (!routeDef?.discoverableFeatures) {
      routeDef = this.findBestMatchingRoute(route);
    }

    if (!routeDef?.discoverableFeatures) {
      return [];
    }

    return routeDef.discoverableFeatures
      // Filter out already discovered (unless showOnce is false)
      .filter(f => !f.showOnce || !this.hasDiscovered(f.id))
      // Filter by feature flags
      .filter(f => !f.featureFlag || this.isFeatureEnabled(f.featureFlag))
      // Sort by priority
      .sort((a, b) => b.priority - a.priority)
      // Limit results
      .slice(0, limit);
  }

  /**
   * Find best matching route for discovery features
   * Example: /leads/inactive-leads matches /leads/all-leads
   */
  private findBestMatchingRoute(currentRoute: string): any {
    const knowledge = this.appKnowledge.getKnowledgeValue();
    if (!knowledge) return null;

    // Get all routes with discovery features
    const routesWithFeatures = Object.values(knowledge.routes)
      .filter(route => route.discoverableFeatures && route.discoverableFeatures.length > 0);

    if (routesWithFeatures.length === 0) return null;

    // Try to find routes that share the same base path
    // Example: /leads/inactive-leads and /leads/all-leads both start with /leads
    const currentParts = currentRoute.split('/').filter(Boolean);

    // Find routes with matching base paths
    const candidates = routesWithFeatures.filter(route => {
      const routeParts = route.path.split('/').filter(Boolean);
      // Match if they share at least the first segment (e.g., both start with 'leads')
      return routeParts.length > 0 && currentParts.length > 0 && routeParts[0] === currentParts[0];
    });

    // Return the first candidate, or null if none found
    return candidates.length > 0 ? candidates[0] : null;
  }

  /**
   * Get all discoverable features for a route (for "See all tips")
   */
  getAllTips(route: string): DiscoverableFeature[] {
    const routeDef = this.appKnowledge.getRoute(route);
    if (!routeDef?.discoverableFeatures) {
      return [];
    }

    return routeDef.discoverableFeatures
      .filter(f => !f.featureFlag || this.isFeatureEnabled(f.featureFlag))
      .sort((a, b) => b.priority - a.priority);
  }

  /**
   * Mark feature as discovered (user clicked Show Me or used it)
   */
  markDiscovered(featureId: string): void {
    this.discoveredFeatures.add(featureId);
    this.discoveredFeatures$.next(new Set(this.discoveredFeatures));
    this.saveDiscoveredFeatures();

    console.log(`[Feature Discovery] Marked as discovered: ${featureId}`);

    // Track analytics (could integrate with Google Analytics, Mixpanel, etc.)
    this.trackAnalytics('feature_discovered', featureId);
  }

  /**
   * Check if user has discovered this feature
   */
  hasDiscovered(featureId: string): boolean {
    return this.discoveredFeatures.has(featureId);
  }

  /**
   * Get observable of discovered features
   */
  getDiscoveredFeatures(): Observable<Set<string>> {
    return this.discoveredFeatures$.asObservable();
  }

  /**
   * Get count of discovered features for a route
   */
  getDiscoveredCount(route: string): number {
    const routeDef = this.appKnowledge.getRoute(route);
    if (!routeDef?.discoverableFeatures) {
      return 0;
    }

    return routeDef.discoverableFeatures
      .filter(f => this.hasDiscovered(f.id))
      .length;
  }

  /**
   * Get total count of features for a route
   */
  getTotalCount(route: string): number {
    const routeDef = this.appKnowledge.getRoute(route);
    return routeDef?.discoverableFeatures?.length || 0;
  }

  /**
   * Check if all features discovered for a route
   */
  allDiscovered(route: string): boolean {
    const total = this.getTotalCount(route);
    const discovered = this.getDiscoveredCount(route);
    return total > 0 && total === discovered;
  }

  /**
   * Reset discoveries (for testing or new user onboarding)
   */
  resetDiscoveries(): void {
    this.discoveredFeatures.clear();
    this.discoveredFeatures$.next(new Set());
    this.saveDiscoveredFeatures();
    console.log('[Feature Discovery] Reset all discoveries');
  }

  /**
   * Reset discoveries for a specific route
   */
  resetRoute(route: string): void {
    const routeDef = this.appKnowledge.getRoute(route);
    if (routeDef?.discoverableFeatures) {
      routeDef.discoverableFeatures.forEach(f => {
        this.discoveredFeatures.delete(f.id);
      });
      this.discoveredFeatures$.next(new Set(this.discoveredFeatures));
      this.saveDiscoveredFeatures();
      console.log(`[Feature Discovery] Reset discoveries for route: ${route}`);
    }
  }

  /**
   * Check if a feature is enabled via feature flag
   */
  private isFeatureEnabled(featureFlag: string): boolean {
    return this.enabledFeatures.has(featureFlag);
  }

  /**
   * Load discovered features from localStorage
   */
  private loadDiscoveredFeatures(): void {
    try {
      const stored = localStorage.getItem('ai-assist-discovered-features');
      if (stored) {
        const features = JSON.parse(stored) as string[];
        this.discoveredFeatures = new Set(features);
        this.discoveredFeatures$.next(new Set(this.discoveredFeatures));
        console.log(`[Feature Discovery] Loaded ${features.length} discovered features`);
      }
    } catch (error) {
      console.error('[Feature Discovery] Failed to load discoveries:', error);
    }
  }

  /**
   * Save discovered features to localStorage
   */
  private saveDiscoveredFeatures(): void {
    try {
      const features = Array.from(this.discoveredFeatures);
      localStorage.setItem('ai-assist-discovered-features', JSON.stringify(features));
    } catch (error) {
      console.error('[Feature Discovery] Failed to save discoveries:', error);
    }
  }

  /**
   * Track analytics event
   */
  private trackAnalytics(event: string, featureId: string): void {
    // TODO: Integrate with analytics service
    console.log(`[Analytics] ${event}: ${featureId}`);

    // Example integrations:
    // this.analytics.track(event, { feature_id: featureId });
    // gtag('event', event, { feature_id: featureId });
  }

  /**
   * Get discovery statistics (for admin/debugging)
   */
  getStats(): {
    totalDiscovered: number;
    totalAvailable: number;
    discoveryRate: number;
    features: string[];
  } {
    const features = Array.from(this.discoveredFeatures);
    const totalAvailable = this.enabledFeatures.size;

    return {
      totalDiscovered: features.length,
      totalAvailable,
      discoveryRate: totalAvailable > 0 ? features.length / totalAvailable : 0,
      features
    };
  }
}
